// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import BigNumber from 'bignumber.js';

// Compact USD formatting for market tiles: $0.72, $16.32M, $7.29B, $1.2T.
export function formatUsd(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) return '--';
	if (value === 0) return '$0';

	const abs = Math.abs(value);
	const units: [number, string][] = [
		[1e12, 'T'],
		[1e9, 'B'],
		[1e6, 'M'],
		[1e3, 'K'],
	];
	for (const [threshold, suffix] of units) {
		if (abs >= threshold) {
			return `$${(value / threshold).toFixed(2)}${suffix}`;
		}
	}
	// Small values: show more precision for sub-dollar prices.
	const digits = abs < 1 ? 4 : 2;
	return `$${value.toFixed(digits)}`;
}

// Volume rendered as a full comma-grouped dollar amount with the fractional
// part broken out so it can be de-emphasized: `$24,065,135` + `.254`.
export function formatVolumeParts(
	value: number | null | undefined,
): { whole: string; fraction: string } | null {
	if (value == null || !Number.isFinite(value)) return null;
	const bn = new BigNumber(value);
	const whole = bn.integerValue(BigNumber.ROUND_DOWN);
	const fraction = bn.minus(whole).abs().toFixed(3).slice(2); // "254"
	return { whole: `$${whole.toFormat(0)}`, fraction };
}

// Signed percent change with a flag for color: `+36.02%` / `-15.58%`.
export function formatPercentChange(
	value: number | null | undefined,
): { text: string; positive: boolean } | null {
	if (value == null || !Number.isFinite(value)) return null;
	const positive = value >= 0;
	return { text: `${positive ? '+' : ''}${value.toFixed(2)}%`, positive };
}

// Base-unit balance → comma-grouped whole-token amount: "309,858,217".
export function formatSupplyAmount(
	baseUnits: string | null | undefined,
	decimals: number | null | undefined,
): string | null {
	if (baseUnits == null || decimals == null) return null;
	return new BigNumber(baseUnits).shiftedBy(-decimals).integerValue(BigNumber.ROUND_DOWN).toFormat(0);
}

// A plain (no `$`) comma-grouped USD amount for the secondary supply line.
export function formatUsdPlain(value: number | null | undefined): string | null {
	if (value == null || !Number.isFinite(value)) return null;
	return new BigNumber(value).integerValue(BigNumber.ROUND_DOWN).toFormat(0);
}

// Compact age since creation, matching the reference: `3y`, `214d`, `5h`.
export function formatCreateTime(ms: number | null | undefined, now: number = Date.now()): string {
	if (ms == null || !Number.isFinite(ms)) return '--';
	const diff = now - ms;
	if (diff < 0) return '--';
	const DAY = 86_400_000;
	const days = Math.floor(diff / DAY);
	if (days >= 365) return `${Math.floor(days / 365)}y`;
	if (days >= 1) return `${days}d`;
	const hours = Math.floor(diff / 3_600_000);
	if (hours >= 1) return `${hours}h`;
	const mins = Math.floor(diff / 60_000);
	if (mins >= 1) return `${mins}m`;
	return '<1m';
}
